/**
 * IMPORTS
 */

import { useQuery } from "@tanstack/react-query";

import { useStoreZustandUserAuth } from "@/store-zustand/user-auth";
import { useStoreZustandVendasCreditos } from "@/store-zustand/vendas-creditos/credits";

import { handleVendasTotalCreditosPorCliente } from "@/domain/use-cases/vendas-creditos";

export interface IPagination {
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
}

export type ITypesCredito = {
  produto_id?: number;
  tipo: string;
  total: string;
};
export type IVendasCreditos = {
  cliente_id: number;
  format: string;
  saldo_total: number;
  creditos: ITypesCredito[];
};
export interface IVendasCreditosResponse {
  code: number;
  status: string;
  message: string;
  data: IVendasCreditos;
}

export function generatePagination(
  currentPage: number,
  lastPage: number,
  delta = 2
): (number | string)[] {
  const range: (number | string)[] = [];
  const left = Math.max(2, currentPage - delta);
  const right = Math.min(lastPage - 1, currentPage + delta);

  range.push(1);

  if (left > 2) {
    range.push("...");
  }

  for (let i = left; i <= right; i++) {
    range.push(i);
  }

  if (right < lastPage - 1) {
    range.push("...");
  }

  if (lastPage > 1) {
    range.push(lastPage);
  }

  return range;
}

/**
 * Buscar transações de vendas de um cliente
 * @param user_cliente_id
 * @param enabled false quando o usuário não tem permissão para ver o saldo
 * @returns
 */
function useGetTotalCreditosPorCliente(user_cliente_id: number, enabled = true) {
  const { currentPage, itemsPerPage, searchItem } = useStoreZustandVendasCreditos();

  const { token: accessToken } = useStoreZustandUserAuth();

  // Chave de cache única baseada na página atual e na quantidade de itens por página
  const queryKey = ["vendas-total-creditos-por-cliente", currentPage, itemsPerPage, searchItem];

  const {
    isLoading,
    error: isError,
    data: vendasCreditosResponse,
  } = useQuery<IVendasCreditosResponse>({
    queryKey,
    queryFn: async () => {
      const response = await handleVendasTotalCreditosPorCliente(accessToken!, user_cliente_id);

      return response;
    },
    enabled,
  });

  return {
    vendasCreditos: vendasCreditosResponse?.data?.creditos ?? [],
    isLoading,
    isError,
  };
}

/**
 * EXPORTS
 */
export { useGetTotalCreditosPorCliente };
